import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  Camera,
  Handshake,
  History,
  Palette,
  Pencil,
  StickyNote,
  Undo2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { useCaptures, type UserCapture } from "@/stores/useCaptures";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { SnapshotHistoryPopover } from "@/lib/SnapshotHistoryPopover";
import {
  getHandshakeContent,
  type HandshakeContent,
  type HandshakePromptAccent,
} from "./handshake-content";
import {
  useHandshakeStrokes,
  type PersistedBoard,
  type Sticky,
  type Stroke,
  type StrokePoint,
} from "./use-handshake-strokes";
import { useHandshakeSnapshot } from "./useSnapshot";

/**
 * Handshake Board — a workshop canvas for capturing Human-Agent
 * handshake protocols. Ported from the `#handshakeModal` block in
 * `_legacy/wd.html` lines 922-1016, which paired a left info-panel
 * (`#handshake-panel-body`, populated by `renderHandshakePanel()` in
 * `_legacy/xaes-content.js` lines 696-743) with a right whiteboard
 * (`#hbDrawCanvas` + `#hbItems` + `#hbOverlay`).
 *
 * Slim React port:
 *   - The vanilla multi-tool toolbar (select / sticky / text / draw /
 *     erase / arrow) collapses to a single freehand draw mode.
 *     Sticky notes, arrows, and text labels are deferred to a follow-up
 *     slice — the data model in `use-handshake-strokes` is already
 *     scoped so adding richer items later is additive, not breaking.
 *   - Persistence runs through `idb-keyval`, keyed by workshop id, so
 *     facilitators don't lose work on reload. Vanilla had no
 *     persistence — `hbSave()` exported to "Captures" instead.
 *
 * Drawing is wired to Pointer Events so mouse, stylus, and touch all
 * route through the same handlers; we capture the pointer on `down`
 * so the stroke stays attached even if the cursor leaves the canvas
 * mid-line. Device-pixel-ratio handling mirrors
 * `src/components/fx/SmokeCanvas.tsx` (`canvas.width = clientWidth *
 * dpr` + `ctx.scale(dpr, dpr)` on every resize). StrictMode-safe via
 * the established `rafId` + `disposed` pattern from `PlasmaCanvas`.
 */

/* ----------------------------- Palette ---------------------------- */

type Swatch = {
  /** Stable id used for keyed rendering and active comparison. */
  id: string;
  /** Display label for tooltip / aria. */
  label: string;
  /** Stroke value applied to the canvas (CSS colour string). */
  value: string;
  /** Static fallback used to render the swatch chip itself. */
  chipBg: string;
};

const SWATCHES: Swatch[] = [
  {
    id: "true-blue",
    label: "True Blue",
    value: "var(--accent-true-blue)",
    chipBg: "var(--accent-true-blue)",
  },
  {
    id: "sky",
    label: "Sky",
    value: "var(--accent-sky)",
    chipBg: "var(--accent-sky)",
  },
  {
    id: "gold",
    label: "Gold",
    value: "var(--accent-gold)",
    chipBg: "var(--accent-gold)",
  },
  {
    id: "melon",
    label: "Melon",
    value: "var(--accent-melon)",
    chipBg: "var(--accent-melon)",
  },
  {
    id: "green",
    label: "Green",
    value: "var(--accent-green)",
    chipBg: "var(--accent-green)",
  },
];

const DEFAULT_STROKE_WIDTH = 2.4;

/**
 * Derive the `r,g,b` triplet variable for a swatch colour value so a
 * free-form sticky gets the same tinted background + border treatment
 * the prompt-seeded stickies use. Falls back to true-blue when the
 * active colour isn't one of the named swatches (e.g. a hex seeded by a
 * prompt pill).
 */
function rgbForColor(color: string): string {
  const swatch = SWATCHES.find((s) => s.value === color);
  return swatch
    ? `var(--accent-${swatch.id}-rgb)`
    : "var(--accent-true-blue-rgb)";
}

/* --------------------------- Info Panel --------------------------- */

/**
 * Map each prompt accent slot to its legacy CSS variable family. We
 * compose the pill chrome from `--accent-<name>` + `--accent-<name>-rgb`
 * so the same record can drive border / background / text inline
 * without spawning a Tailwind utility per state.
 */
const PROMPT_ACCENT_VARS: Record<
  HandshakePromptAccent,
  { color: string; rgb: string }
> = {
  orange: { color: "var(--accent-gold)", rgb: "var(--accent-gold-rgb)" },
  blue: {
    color: "var(--accent-true-blue)",
    rgb: "var(--accent-true-blue-rgb)",
  },
  green: { color: "var(--accent-green)", rgb: "var(--accent-green-rgb)" },
  melon: { color: "var(--accent-melon)", rgb: "var(--accent-melon-rgb)" },
  cyan: { color: "var(--accent-sky)", rgb: "var(--accent-sky-rgb)" },
};

// Width and the divider border live on `.activity-rail` in index.css so
// the narrow-screen rule (rail folds above the canvas) can override them.
const INFO_RAIL_STYLE: CSSProperties = {
  background: "var(--bg-panel)",
  padding: "10px 12px",
  gap: 8,
};

const INFO_RAIL_DIVIDER_STYLE: CSSProperties = {
  height: 1,
  flexShrink: 0,
  background: "rgba(var(--accent-true-blue-rgb), 0.20)",
  border: 0,
  margin: 0,
};

const INFO_RAIL_EYEBROW_STYLE: CSSProperties = {
  fontSize: "var(--fs-label)",
  letterSpacing: 2,
  textTransform: "uppercase",
  fontWeight: 700,
  color: "var(--accent-sky)",
};

const INFO_RAIL_BODY_STYLE: CSSProperties = {
  fontSize: "var(--fs-label)",
  lineHeight: 1.35,
  color: "var(--text-secondary)",
  margin: 0,
};

/**
 * Payload emitted when a prompt pill is clicked. Carries everything
 * downstream needs to (a) set the active drawing colour and (b) drop
 * a sticky note carrying the prompt's long-form text + tinted bg —
 * matching the dual-effect vanilla behaviour in `hbAddSticky()` at
 * `_legacy/xaes-nav.js` line 939.
 */
type PromptPick = {
  text: string;
  color: string;
  rgb: string;
};

type HandshakeInfoPanelProps = {
  /** Per-workshop handshake content, or `null` when unauthored. */
  content: HandshakeContent | null;
  /** Workshop id — shown in the empty state for context. */
  workshopId: string;
  /** Currently selected stroke colour — drives the active-pill outline. */
  activeColor: string;
  /** Called when a prompt pill is clicked; receives the prompt's seed data. */
  onPickPrompt: (pick: PromptPick) => void;
};

function HandshakeInfoPanel({
  content,
  workshopId,
  activeColor,
  onPickPrompt,
}: HandshakeInfoPanelProps) {
  // RT-CANVAS — unauthored workshops render an empty info rail; the board
  // itself stays fully usable for free-form facilitation.
  if (!content) {
    return (
      <aside
        className="activity-rail flex h-full flex-col overflow-hidden"
        style={INFO_RAIL_STYLE}
      >
        <section style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <div style={INFO_RAIL_EYEBROW_STYLE}>Objective</div>
          <p style={{ ...INFO_RAIL_BODY_STYLE, color: "var(--text-muted)" }}>
            No handshake activity is configured for{" "}
            <code>{workshopId}</code> yet. The board is still yours — sketch
            flows, drop sticky notes, and capture the team's protocol.
          </p>
        </section>
      </aside>
    );
  }
  const h = content;
  return (
    <aside
      className="activity-rail flex h-full flex-col overflow-hidden"
      style={INFO_RAIL_STYLE}
    >
      <section style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={INFO_RAIL_EYEBROW_STYLE}>Objective</div>
        <h3
          style={{
            fontSize: "var(--fs-sub)",
            fontWeight: 700,
            letterSpacing: 0.5,
            lineHeight: 1.25,
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          {h.objective}
        </h3>
        <p style={INFO_RAIL_BODY_STYLE}>{h.description}</p>
      </section>

      <hr style={INFO_RAIL_DIVIDER_STYLE} />

      <section style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={INFO_RAIL_EYEBROW_STYLE}>Key Questions</div>
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {h.keyQuestions.map((q) => (
            <li
              key={q}
              style={{
                display: "flex",
                gap: 6,
                alignItems: "flex-start",
                ...INFO_RAIL_BODY_STYLE,
              }}
            >
              <span
                aria-hidden
                style={{
                  flexShrink: 0,
                  color: "var(--accent-true-blue)",
                  fontWeight: 700,
                }}
              >
                →
              </span>
              <span>{q}</span>
            </li>
          ))}
        </ul>
      </section>

      <hr style={INFO_RAIL_DIVIDER_STYLE} />

      <section style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={INFO_RAIL_EYEBROW_STYLE}>Prompts</div>
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {h.prompts.map((p) => {
            const tone = PROMPT_ACCENT_VARS[p.accentVar];
            // The pill's "active" state compares its seed colour against
            // the canvas' current stroke colour. Vanilla seeded a sticky
            // note from the same colour token, so reusing it here keeps
            // the click semantics one-to-one with the legacy intent
            // while extending the affordance: pick a prompt, paint with
            // its accent.
            const isActive = p.color === activeColor;
            return (
              <li key={p.label} style={{ margin: 0, padding: 0 }}>
                <button
                  type="button"
                  onClick={() =>
                    onPickPrompt({
                      text: p.text,
                      color: p.color,
                      rgb: tone.rgb,
                    })
                  }
                  aria-pressed={isActive}
                  title={p.text}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "3px 8px",
                    background: isActive
                      ? `rgba(${tone.rgb}, 0.22)`
                      : `rgba(${tone.rgb}, 0.10)`,
                    border: isActive
                      ? `1px solid ${tone.color}`
                      : `1px solid rgba(${tone.rgb}, 0.30)`,
                    boxShadow: isActive
                      ? `0 0 0 1px rgba(${tone.rgb}, 0.40), 0 1px 4px rgba(${tone.rgb}, 0.20)`
                      : "none",
                    borderRadius: 6,
                    color: tone.color,
                    fontSize: "var(--fs-label)",
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    lineHeight: 1.3,
                    cursor: "pointer",
                    transition:
                      "background 0.15s, border-color 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (isActive) return;
                    e.currentTarget.style.background = `rgba(${tone.rgb}, 0.16)`;
                  }}
                  onMouseLeave={(e) => {
                    if (isActive) return;
                    e.currentTarget.style.background = `rgba(${tone.rgb}, 0.10)`;
                  }}
                >
                  {p.label}
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <hr style={INFO_RAIL_DIVIDER_STYLE} />

      <p
        style={{
          fontSize: "var(--fs-label)",
          lineHeight: 1.35,
          color: "var(--text-muted)",
          margin: 0,
        }}
      >
        {h.boardHint}
      </p>
    </aside>
  );
}

/* --------------------------- Canvas hook -------------------------- */

/**
 * Manage the canvas: keep it sized to its container at device-pixel
 * ratio, and redraw all completed strokes plus any in-progress one
 * whenever the inputs change.
 *
 * Returns refs the parent attaches to the `<canvas>` and the
 * positioning wrapper, plus a `redraw` callback the pointer handlers
 * use to repaint mid-stroke without going through a state update.
 */
function useCanvasPainter(
  strokes: Stroke[],
  activeStrokeRef: MutableRefObject<Stroke | null>,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Resolved CSS colour cache so `var(--accent-…)` references render
  // on a 2D context (which can't consume custom properties directly).
  // We resolve via a hidden helper element to support any valid CSS
  // colour syntax, not just hex.
  const colorResolverRef = useRef<HTMLSpanElement | null>(null);

  function resolveColor(input: string): string {
    if (!input.startsWith("var(")) return input;
    let helper = colorResolverRef.current;
    if (!helper) {
      helper = document.createElement("span");
      helper.style.display = "none";
      document.body.appendChild(helper);
      colorResolverRef.current = helper;
    }
    helper.style.color = "";
    helper.style.color = input;
    const computed = getComputedStyle(helper).color;
    return computed || input;
  }

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const paint = (stroke: Stroke) => {
      if (stroke.points.length === 0) return;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = stroke.width;
      ctx.strokeStyle = resolveColor(stroke.color);
      ctx.beginPath();
      const [first, ...rest] = stroke.points;
      ctx.moveTo(first!.x, first!.y);
      if (rest.length === 0) {
        // Single tap — draw a dot so the user gets feedback.
        ctx.lineTo(first!.x + 0.01, first!.y + 0.01);
      } else {
        for (const p of rest) ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
    };

    for (const s of strokes) paint(s);
    if (activeStrokeRef.current) paint(activeStrokeRef.current);
  }, [strokes, activeStrokeRef]);

  // Resize / DPR handling: ResizeObserver re-runs the setup whenever
  // the wrapper changes size; window resize is covered transitively.
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let disposed = false;

    function resize() {
      if (disposed || !canvas || !wrapper || !ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const w = wrapper.clientWidth;
      const h = wrapper.clientHeight;
      if (!w || !h) return;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      redraw();
    }

    const ro = new ResizeObserver(resize);
    ro.observe(wrapper);
    resize();

    return () => {
      disposed = true;
      ro.disconnect();
    };
  }, [redraw]);

  // Redraw whenever the strokes list changes (undo/clear/add).
  useEffect(() => {
    redraw();
  }, [redraw]);

  // Tear down the colour-resolver helper on unmount.
  useEffect(() => {
    return () => {
      if (colorResolverRef.current) {
        colorResolverRef.current.remove();
        colorResolverRef.current = null;
      }
    };
  }, []);

  return { canvasRef, wrapperRef, redraw };
}

/* ----------------------------- Board ------------------------------ */

type HandshakeBoardProps = {
  workshopId: string;
  className?: string;
  /** Render a CLOSE pill in the top bar that fires this callback. */
  onClose?: () => void;
};

/**
 * The dot-grid CSS background is implemented via a layered
 * `radial-gradient` so it scales cleanly on any DPR without us having
 * to encode an SVG — matches the visual of the vanilla `#hbDotGrid`
 * pattern in `_legacy/wd.html` lines 985-989.
 */
const DOT_GRID_STYLE: CSSProperties = {
  backgroundImage:
    "radial-gradient(circle, color-mix(in srgb, var(--accent-true-blue) 18%, transparent) 1px, transparent 1px)",
  backgroundSize: "28px 28px",
  backgroundPosition: "0 0",
};

function makeStrokeId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `stroke-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function makeStickyId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `sticky-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Default rendered sticky width, in px. Matches the legacy 140px feel. */
const STICKY_WIDTH = 140;
/** Minimum margin from the board's edges when randomising sticky drops. */
const STICKY_MARGIN = 24;
/** Heuristic vertical reservation when clamping random drops near the bottom. */
const STICKY_DROP_HEIGHT = 80;

/**
 * Pick a random `(x, y)` inside the board, clamped so the sticky's
 * left edge stays ≥ STICKY_MARGIN and its right edge stays ≤
 * width − STICKY_WIDTH − STICKY_MARGIN. Mirrors the vanilla random
 * range in `hbAddSticky()` (`_legacy/xaes-nav.js` line 939) but
 * adapted to the React port's sticky width.
 */
function randomStickyPosition(
  width: number,
  height: number,
): { x: number; y: number } {
  const maxX = Math.max(
    STICKY_MARGIN,
    width - STICKY_WIDTH - STICKY_MARGIN,
  );
  const maxY = Math.max(
    STICKY_MARGIN,
    height - STICKY_DROP_HEIGHT - STICKY_MARGIN,
  );
  const rangeX = Math.max(0, maxX - STICKY_MARGIN);
  const rangeY = Math.max(0, maxY - STICKY_MARGIN);
  return {
    x: STICKY_MARGIN + Math.random() * rangeX,
    y: STICKY_MARGIN + Math.random() * rangeY,
  };
}

export function HandshakeBoard({
  workshopId,
  className,
  onClose,
}: HandshakeBoardProps) {
  const {
    strokes,
    stickies,
    actionLog,
    addStroke,
    addSticky,
    updateSticky,
    removeSticky,
    clear,
    undo,
    replaceAll,
  } = useHandshakeStrokes(workshopId);

  const [activeColor, setActiveColor] = useState<string>(SWATCHES[0]!.value);
  // Active board tool. `draw` paints freehand strokes; `sticky` drops a
  // free-form note at the click point and opens it for inline editing.
  const [tool, setTool] = useState<"draw" | "sticky">("draw");
  // Id of the sticky currently being text-edited (inline textarea), or
  // null when none is open.
  const [editingStickyId, setEditingStickyId] = useState<string | null>(null);
  const activeStrokeRef = useRef<Stroke | null>(null);

  // RT-CANVAS — read content + title through the per-workshop getter so
  // the board generalises beyond WS4. `content` is null for unauthored
  // workshops (info rail shows the empty state); `longTitle` de-hardcodes
  // the footer that used to read "Human-Agent Ways of Working" verbatim.
  const content = getHandshakeContent(workshopId);
  const longTitle =
    WORKSHOPS.find((w) => w.id === workshopId)?.longTitle ?? workshopId;

  const { canvasRef, wrapperRef, redraw } = useCanvasPainter(
    strokes,
    activeStrokeRef,
  );

  /* ---- Snapshot system ---------------------------------------------
   *
   * Wires the shared snapshot infra to this board:
   *   - `captureBlob` renders the live canvas + sticky layer onto a
   *     hidden offscreen canvas (see `useSnapshot.ts`).
   *   - `boardState` is the persisted envelope sent into `stateJson`
   *     so restoring round-trips the strokes + stickies + action log
   *     verbatim, not just the visual.
   *   - Auto-save debounces 3s after the most recent change. The
   *     timer ref + cleanup is StrictMode-safe (matches the
   *     `XaesAnalyst.tsx` save-flash debounce pattern).
   */
  const boardState: PersistedBoard = { strokes, stickies, actionLog };
  const applyRestoredBoard = useCallback(
    (next: PersistedBoard) => {
      replaceAll(next);
    },
    [replaceAll],
  );
  const snapshot = useHandshakeSnapshot({
    workshopId,
    canvasRef,
    wrapperRef,
    stickies,
    boardState,
    applyRestoredBoard,
  });

  const auth = useAuth();

  /**
   * Auto-save debounce — fires once 3s after the most recent stroke
   * / sticky mutation, then resets when the next change arrives. We
   * stash the timer id in a ref so StrictMode's double-invoke can
   * cancel cleanly without spawning duplicate writes.
   *
   * The first hydration pass also trips this effect, but with an
   * empty board the captured PNG is a no-op snapshot; we skip in
   * that case to avoid littering history with blank tiles.
   */
  const autoSaveTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (strokes.length === 0 && stickies.length === 0) {
      // Empty board — nothing to capture. Cancel any in-flight timer
      // (e.g. user just hit Clear) so we don't snapshot the blank.
      if (autoSaveTimerRef.current !== null) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }
    if (autoSaveTimerRef.current !== null) {
      window.clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = window.setTimeout(() => {
      autoSaveTimerRef.current = null;
      void snapshot.saveAuto();
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current !== null) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
    // We intentionally depend on the *contents* of strokes/stickies
    // (via their length + action log) rather than the array identity
    // itself; `actionLog.length` rises with every mutation including
    // sticky-position updates, so it's the cleanest single signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionLog.length, strokes.length, stickies.length]);

  /**
   * Manual "Save snapshot" handler — captures, stashes the blob, and
   * adds a Captures-grid entry. The capture carries `blobKey` so the
   * `CapturesModal` lightbox can pull the PNG directly from
   * blob-store without rebuilding the visual from JSON.
   *
   * Migration note: this used to route to `useAssetLocker` (the
   * Deliverables chip grid). Snapshots are now scoped to the
   * Captures surface so the Deliverables list stays as the
   * facilitator-curated 6-card legacy set; see
   * `stores/useCaptures.ts` for the rationale.
   */
  const handleManualSave = useCallback(async () => {
    const meta = await snapshot.saveManual();
    if (!meta) {
      toast({
        title: "Snapshot failed",
        description: "Couldn't capture the board — try again.",
      });
      return;
    }
    // Captures scope is workshop-id strings; the HAH board is
    // mounted under WS4 today but the lock-down lives at the store
    // level (see `useCaptures.ts` sanitise). We pass the live
    // `workshopId` through verbatim — if it's not a known workshop
    // the store's `add()` no-ops on the next hydrate.
    const stamp = new Date(meta.createdAt).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
    const timestamp = new Date(meta.createdAt).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const addedBy = auth.user?.displayName ?? auth.user?.username ?? "anon";
    const capture: UserCapture = {
      id: meta.id,
      workshopId,
      title: `Handshake snapshot — ${stamp}`,
      activity: "Activity 2 — HAH",
      timestamp,
      kind: "whiteboard",
      accent: "true-blue",
      source: "Saved",
      caption: `Handshake board snapshot saved by ${addedBy}.`,
      blobKey: meta.blobKey,
      mimeType: "image/png",
      addedAt: meta.createdAt,
      addedBy,
    };
    // `HandshakeBoard` accepts `workshopId: string` (the board can be
    // hosted under any workshop scope) but the Captures store is
    // keyed by the strict `Workshop["id"]` union. We narrow via cast
    // — the store's sanitiser ignores any out-of-union value on the
    // next hydrate, so this is safe even if a stray scope sneaks in.
    useCaptures
      .getState()
      .add(workshopId as Workshop["id"], capture);
    toast({
      title: "Snapshot saved",
      description: capture.title,
    });
  }, [snapshot, auth.user, workshopId]);

  /**
   * Drop a new sticky inside the board's current bounds. Falls back
   * to a deterministic centred position if the wrapper hasn't been
   * laid out yet (defensive — `addSticky` should never be called pre-
   * mount, but the ref-read is async-safe regardless).
   */
  const handlePickPrompt = useCallback(
    ({ text, color, rgb }: PromptPick) => {
      setActiveColor(color);
      const wrapper = wrapperRef.current;
      let position = { x: STICKY_MARGIN, y: STICKY_MARGIN };
      if (wrapper) {
        const rect = wrapper.getBoundingClientRect();
        position = randomStickyPosition(rect.width, rect.height);
      }
      addSticky({
        id: makeStickyId(),
        text,
        color,
        rgb,
        x: position.x,
        y: position.y,
      });
    },
    [addSticky, wrapperRef],
  );

  /**
   * Convert a pointer event into canvas-local coordinates.
   */
  function pointFromEvent(e: ReactPointerEvent<HTMLDivElement>): StrokePoint {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  /** Commit an inline sticky edit. Empty text drops the note entirely
   *  (so a freshly-dropped sticky the user clicks away from doesn't
   *  leave a blank box behind). */
  function commitSticky(id: string, value: string): void {
    const text = value.trim();
    if (text) {
      updateSticky(id, { text });
    } else {
      removeSticky(id);
    }
    setEditingStickyId(null);
  }

  function handlePointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.button !== undefined && e.button !== 0) return;

    // Sticky tool — drop a free-form note at the click point and open
    // it for editing. A click while a note is already being edited just
    // commits it (the textarea's blur handler does the save), so don't
    // spawn a second note in that case.
    if (tool === "sticky") {
      if (editingStickyId !== null) return;
      // Suppress the browser's default mousedown focus action. React
      // flushes this discrete event synchronously, so the note's
      // auto-focused textarea mounts *during* the dispatch — the
      // default action then re-focused the board, the textarea blurred
      // with empty text, and commitSticky deleted the brand-new note.
      // Net effect without this: the sticky tool visibly "did nothing".
      e.preventDefault();
      const point = pointFromEvent(e);
      const { x, y } = clampToBoard(point.x, point.y, STICKY_WIDTH);
      const id = makeStickyId();
      addSticky({
        id,
        text: "",
        color: activeColor,
        rgb: rgbForColor(activeColor),
        x,
        y,
      });
      setEditingStickyId(id);
      return;
    }

    e.currentTarget.setPointerCapture(e.pointerId);
    activeStrokeRef.current = {
      id: makeStrokeId(),
      color: activeColor,
      width: DEFAULT_STROKE_WIDTH,
      points: [pointFromEvent(e)],
    };
    redraw();
  }

  function handlePointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const current = activeStrokeRef.current;
    if (!current) return;
    current.points.push(pointFromEvent(e));
    redraw();
  }

  function handlePointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    const current = activeStrokeRef.current;
    if (!current) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Pointer was never captured (e.g., pen left the surface) — fine.
    }
    activeStrokeRef.current = null;
    if (current.points.length > 0) {
      addStroke(current);
    } else {
      redraw();
    }
  }

  /* ---- Sticky drag --------------------------------------------------
   *
   * Each sticky carries pointer-down/move/up handlers that capture
   * the pointer on the sticky element itself. We track the drag in
   * a ref (no re-render per frame) and commit the final position to
   * the persisted store on pointer-up. The wrapper's pointer events
   * already swallow drawing strokes from happening on stickies via
   * stopPropagation, so dragging never accidentally also paints.
   */
  type DragState = {
    pointerId: number;
    stickyId: string;
    offsetX: number;
    offsetY: number;
  };
  const dragStateRef = useRef<DragState | null>(null);
  const [dragPreview, setDragPreview] = useState<{
    id: string;
    x: number;
    y: number;
  } | null>(null);

  const clampToBoard = useCallback(
    (x: number, y: number, width: number): { x: number; y: number } => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return { x, y };
      const rect = wrapper.getBoundingClientRect();
      const maxX = Math.max(0, rect.width - width);
      const maxY = Math.max(0, rect.height - STICKY_DROP_HEIGHT / 2);
      return {
        x: Math.min(Math.max(0, x), maxX),
        y: Math.min(Math.max(0, y), maxY),
      };
    },
    [wrapperRef],
  );

  function handleStickyPointerDown(
    e: ReactPointerEvent<HTMLDivElement>,
    sticky: Sticky,
  ) {
    if (e.button !== undefined && e.button !== 0) return;
    e.stopPropagation();
    const target = e.currentTarget;
    try {
      target.setPointerCapture(e.pointerId);
    } catch {
      // Pointer capture is best-effort; drag still works via the
      // captured-by-default event flow on most browsers.
    }
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const wrapperRect = wrapper.getBoundingClientRect();
    dragStateRef.current = {
      pointerId: e.pointerId,
      stickyId: sticky.id,
      offsetX: e.clientX - wrapperRect.left - sticky.x,
      offsetY: e.clientY - wrapperRect.top - sticky.y,
    };
    setDragPreview({ id: sticky.id, x: sticky.x, y: sticky.y });
  }

  function handleStickyPointerMove(
    e: ReactPointerEvent<HTMLDivElement>,
    sticky: Sticky,
  ) {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.stopPropagation();
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const wrapperRect = wrapper.getBoundingClientRect();
    const rawX = e.clientX - wrapperRect.left - drag.offsetX;
    const rawY = e.clientY - wrapperRect.top - drag.offsetY;
    const { x, y } = clampToBoard(rawX, rawY, sticky.width ?? STICKY_WIDTH);
    setDragPreview({ id: drag.stickyId, x, y });
  }

  function handleStickyPointerUp(
    e: ReactPointerEvent<HTMLDivElement>,
    sticky: Sticky,
  ) {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.stopPropagation();
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Already released.
    }
    const preview = dragPreview;
    dragStateRef.current = null;
    setDragPreview(null);
    if (preview && preview.id === drag.stickyId) {
      if (preview.x !== sticky.x || preview.y !== sticky.y) {
        updateSticky(sticky.id, { x: preview.x, y: preview.y });
      }
    }
  }

  const totalItems = strokes.length + stickies.length;

  return (
    <div
      className={cn("flex h-full w-full flex-col", className)}
      style={{ background: "var(--bg-panel)" }}
    >
      {/* Combined top bar — title + tool toolbar + close. Mirrors the
          legacy `bar-top` row at `_legacy/wd.html` lines 927-970 so the
          modal reads as a single facilitator console rather than the
          earlier nested panel-in-panel chrome. */}
      <div
        className="flex flex-shrink-0 flex-wrap items-center justify-between"
        style={{
          gap: 12,
          padding: "10px 14px",
          background: "var(--bg-panel)",
          borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.20)",
        }}
      >
        <div className="flex items-center" style={{ gap: 10 }}>
          <Handshake
            aria-hidden
            style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }}
          />
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "var(--fs-sub)",
              fontWeight: 700,
              letterSpacing: 2,
              color: "var(--text-primary)",
            }}
          >
            HUMAN-AGENT HANDSHAKE
          </span>
          <span
            style={{
              padding: "1px 8px",
              background: "rgba(var(--accent-true-blue-rgb), 0.12)",
              border: "1px solid var(--accent-true-blue)",
              borderRadius: 10,
              fontSize: "var(--fs-label)",
              color: "var(--accent-true-blue)",
              fontWeight: 600,
              letterSpacing: 1,
            }}
          >
            OBJECTIVE 1
          </span>
        </div>

        <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
          {/* TOOL — the two live tools, as labelled mode buttons. The
              legacy select / text / erase / arrow slots used to render
              as disabled placeholders here; UAT read the grey icons
              (and the icon-only sticky button) as broken, so the
              toolbar now only shows what works and says what it is. */}
          <span style={EYEBROW_LABEL_STYLE}>TOOL</span>
          <ToolIconButton
            icon={StickyNote}
            label="Sticky note — click the board to place a note"
            text="Sticky"
            active={tool === "sticky"}
            onClick={() => setTool("sticky")}
          />
          <ToolIconButton
            icon={Pencil}
            label="Draw — drag on the board to sketch"
            text="Draw"
            active={tool === "draw"}
            onClick={() => setTool("draw")}
          />

          <Divider />

          <span style={EYEBROW_LABEL_STYLE}>
            <Palette
              aria-hidden
              strokeWidth={1.6}
              style={{ width: 12, height: 12, marginRight: 2 }}
            />
            COLOUR
          </span>
          <div
            role="radiogroup"
            aria-label="Stroke colour"
            className="flex items-center"
            style={{ gap: 6 }}
          >
            {SWATCHES.map((s) => {
              const isActive = s.value === activeColor;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  aria-label={s.label}
                  title={s.label}
                  onClick={() => setActiveColor(s.value)}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: s.chipBg,
                    border: isActive
                      ? "2px solid var(--text-primary)"
                      : "1px solid hsl(var(--border))",
                    transform: isActive ? "scale(1.08)" : "scale(1)",
                    transition: "transform 0.15s, border-color 0.15s",
                    cursor: "pointer",
                    padding: 0,
                  }}
                />
              );
            })}
          </div>

          <Divider />

          <button
            type="button"
            onClick={undo}
            disabled={totalItems === 0}
            title="Undo last action"
            aria-label="Undo last action"
            style={TOOLBAR_BTN_STYLE}
            onMouseEnter={onToolbarBtnHover}
            onMouseLeave={onToolbarBtnLeave}
          >
            <Undo2
              aria-hidden
              strokeWidth={1.8}
              style={{ width: 12, height: 12 }}
            />
            UNDO
          </button>

          <button
            type="button"
            onClick={clear}
            disabled={totalItems === 0}
            title="Clear board"
            aria-label="Clear board"
            style={TOOLBAR_DANGER_BTN_STYLE}
            onMouseEnter={onDangerBtnHover}
            onMouseLeave={onDangerBtnLeave}
          >
            CLEAR
          </button>

          <button
            type="button"
            onClick={() => void handleManualSave()}
            title="Save current board to Asset Locker"
            aria-label="Save snapshot to Asset Locker"
            style={SAVE_TO_CAPTURES_STYLE}
            onMouseEnter={onSaveToCapturesHover}
            onMouseLeave={onSaveToCapturesLeave}
          >
            <Camera
              aria-hidden
              strokeWidth={1.8}
              style={{ width: 12, height: 12 }}
            />
            SAVE TO CAPTURES
          </button>

          <SnapshotHistoryPopover
            snapshots={snapshot.snapshots}
            onRestore={(id) => void snapshot.restore(id)}
            onRemove={(id) => void snapshot.remove(id)}
          >
            <button
              type="button"
              title="Browse snapshot history"
              aria-label="Browse snapshot history"
              style={TOOLBAR_BTN_STYLE}
              onMouseEnter={onToolbarBtnHover}
              onMouseLeave={onToolbarBtnLeave}
            >
              <History
                aria-hidden
                strokeWidth={1.8}
                style={{ width: 12, height: 12 }}
              />
              HISTORY
              {snapshot.snapshots.length > 0 && (
                <span
                  style={{
                    marginLeft: 4,
                    padding: "0 5px",
                    borderRadius: 6,
                    background:
                      "rgba(var(--accent-true-blue-rgb), 0.18)",
                    color: "var(--accent-true-blue)",
                    fontSize: "var(--fs-micro)",
                    lineHeight: 1.4,
                  }}
                >
                  {snapshot.snapshots.length}
                </span>
              )}
            </button>
          </SnapshotHistoryPopover>

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="modal-close-btn"
              aria-label="Close"
            >
              <X aria-hidden style={{ width: 12, height: 12 }} />
              CLOSE
            </button>
          )}
        </div>
      </div>

      {/* Body: 260px objective panel + canvas. Drops the gap so the
          left rail sits flush against the canvas, matching the legacy
          `border-right` divider treatment at `_legacy/wd.html` line 976.
          `.activity-body` folds the rail above the canvas below 1024px. */}
      <div className="activity-body flex min-h-0 flex-1 overflow-hidden">
        <HandshakeInfoPanel
          content={content}
          workshopId={workshopId}
          activeColor={activeColor}
          onPickPrompt={handlePickPrompt}
        />

        <div
          ref={wrapperRef}
          className="relative min-h-[320px] flex-1 overflow-hidden touch-none"
          style={{
            backgroundColor: "var(--bg-panel)",
            backgroundImage: DOT_GRID_STYLE.backgroundImage,
            backgroundSize: DOT_GRID_STYLE.backgroundSize,
            backgroundPosition: DOT_GRID_STYLE.backgroundPosition,
            // Signal the armed tool — UAT testers clicked the sticky
            // toolbar button and read the (correct) no-op as broken; the
            // note drops on the *next board click*, so show a placement
            // cursor while the sticky tool is armed.
            cursor: tool === "sticky" ? "copy" : "crosshair",
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          role="application"
          aria-label="Handshake drawing canvas"
        >
          <canvas
            ref={canvasRef}
            className="pointer-events-none absolute inset-0"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{ zIndex: 2 }}
            aria-label="Sticky notes"
          >
            {stickies.map((sticky) => {
              const isDragging =
                dragPreview !== null && dragPreview.id === sticky.id;
              const isEditing = editingStickyId === sticky.id;
              const renderX = isDragging ? dragPreview.x : sticky.x;
              const renderY = isDragging ? dragPreview.y : sticky.y;
              const renderWidth = sticky.width ?? STICKY_WIDTH;
              return (
                <div
                  key={sticky.id}
                  role="note"
                  // While editing, drop the drag handlers so the pointer
                  // reaches the textarea cleanly (text select + focus).
                  onPointerDown={
                    isEditing
                      ? undefined
                      : (e) => handleStickyPointerDown(e, sticky)
                  }
                  onPointerMove={
                    isEditing
                      ? undefined
                      : (e) => handleStickyPointerMove(e, sticky)
                  }
                  onPointerUp={
                    isEditing
                      ? undefined
                      : (e) => handleStickyPointerUp(e, sticky)
                  }
                  onPointerCancel={
                    isEditing
                      ? undefined
                      : (e) => handleStickyPointerUp(e, sticky)
                  }
                  onDoubleClick={() => setEditingStickyId(sticky.id)}
                  style={{
                    position: "absolute",
                    left: renderX,
                    top: renderY,
                    width: renderWidth,
                    background: `rgba(${sticky.rgb}, 0.18)`,
                    border: `1px solid rgba(${sticky.rgb}, 0.4)`,
                    color: sticky.color,
                    borderRadius: 6,
                    padding: "8px 10px",
                    fontSize: "var(--fs-label)",
                    fontWeight: 600,
                    lineHeight: 1.4,
                    letterSpacing: 0.5,
                    cursor: isEditing
                      ? "text"
                      : isDragging
                        ? "grabbing"
                        : "grab",
                    pointerEvents: "auto",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    userSelect: isEditing ? "text" : "none",
                    touchAction: "none",
                    transition: isDragging ? "none" : "box-shadow 0.15s",
                  }}
                >
                  {isEditing ? (
                    <textarea
                      autoFocus
                      defaultValue={sticky.text}
                      rows={2}
                      placeholder="Type a note…"
                      onPointerDown={(e) => e.stopPropagation()}
                      onBlur={(e) => commitSticky(sticky.id, e.target.value)}
                      onKeyDown={(e) => {
                        if (
                          (e.key === "Enter" && !e.shiftKey) ||
                          e.key === "Escape"
                        ) {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                      style={{
                        width: "100%",
                        background: "transparent",
                        border: 0,
                        outline: "none",
                        resize: "none",
                        padding: 0,
                        margin: 0,
                        color: sticky.color,
                        fontFamily: "inherit",
                        fontSize: "var(--fs-label)",
                        fontWeight: 600,
                        lineHeight: 1.4,
                        letterSpacing: 0.5,
                      }}
                    />
                  ) : sticky.text ? (
                    sticky.text
                  ) : (
                    <span style={{ opacity: 0.55, fontStyle: "italic" }}>
                      Empty note
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {totalItems === 0 && (
            <div
              className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
              style={{ gap: 8, textAlign: "center" }}
            >
              <Pencil
                aria-hidden
                strokeWidth={1.4}
                style={{
                  width: 32,
                  height: 32,
                  color: "rgba(var(--accent-true-blue-rgb), 0.18)",
                }}
              />
              <span
                style={{
                  fontSize: "var(--fs-base)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: "rgba(124, 131, 140, 0.55)",
                }}
              >
                Select a tool and click to begin
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer (legacy bar-btm): breadcrumb + items counter. */}
      <div
        className="flex flex-shrink-0 items-center"
        style={{
          gap: 8,
          padding: "8px 14px",
          background: "var(--bg-panel)",
          borderTop: "1px solid rgba(var(--accent-true-blue-rgb), 0.20)",
        }}
      >
        <span
          style={{
            fontSize: "var(--fs-label)",
            letterSpacing: 2,
            textTransform: "uppercase",
            color: "var(--text-muted)",
          }}
        >
          XAES Phase Zero
        </span>
        <span
          aria-hidden
          style={{
            fontSize: "var(--fs-label)",
            color: "rgba(var(--accent-true-blue-rgb), 0.4)",
          }}
        >
          |
        </span>
        <span
          style={{
            fontSize: "var(--fs-label)",
            letterSpacing: 1,
            color: "var(--accent-true-blue)",
          }}
        >
          {workshopId} — {longTitle}
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontSize: "var(--fs-label)",
            letterSpacing: 1,
            color: "var(--text-muted)",
          }}
        >
          {totalItems} {totalItems === 1 ? "ITEM" : "ITEMS"}
        </span>
      </div>
    </div>
  );
}

/* ----------------------- Toolbar sub-components -------------------- */

/**
 * Small icon-only tool button. Active state lights the icon in
 * true-blue with a tinted background; disabled state dims to 0.4
 * opacity and removes interactivity. Visual placeholders for the
 * legacy tools we haven't ported yet keep the toolbar shape consistent
 * with `_legacy/wd.html` lines 936-953 without lying about behaviour.
 */
type ToolIconProps = {
  icon: typeof StickyNote;
  /** Full tooltip / screen-reader label, including the usage hint. */
  label: string;
  /** Short visible caption beside the glyph. */
  text: string;
  active: boolean;
  onClick: () => void;
};

function ToolIconButton({
  icon: Icon,
  label,
  text,
  active,
  onClick,
}: ToolIconProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        height: 24,
        padding: "0 8px",
        background: active
          ? "rgba(var(--accent-true-blue-rgb), 0.18)"
          : "transparent",
        border: active
          ? "1px solid rgba(var(--accent-true-blue-rgb), 0.5)"
          : "1px solid hsl(var(--border))",
        borderRadius: 6,
        color: active ? "var(--accent-true-blue)" : "var(--text-secondary)",
        cursor: "pointer",
        transition: "background 0.15s, border-color 0.15s, color 0.15s",
      }}
    >
      <Icon aria-hidden strokeWidth={1.7} style={{ width: 14, height: 14 }} />
      <span
        style={{
          fontSize: "var(--fs-label)",
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: "uppercase",
        }}
      >
        {text}
      </span>
    </button>
  );
}

function Divider() {
  return (
    <span
      aria-hidden
      style={{
        width: 1,
        height: 18,
        background: "rgba(var(--accent-true-blue-rgb), 0.3)",
        margin: "0 4px",
      }}
    />
  );
}

const EYEBROW_LABEL_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: "var(--fs-label)",
  letterSpacing: 1.5,
  textTransform: "uppercase",
  fontWeight: 600,
  color: "var(--text-muted)",
  marginRight: 2,
};

/* ---------------------- Inline toolbar styling -------------------- */

/**
 * Pill button used for Undo / counter chrome. Mirrors `.modal-close-btn`
 * structure (`_legacy/wd.html` lines 763-782) but lighter so it sits
 * inside the toolbar row rather than the modal header. Hover swaps
 * the border + text colour to `--accent-true-blue`, matching the
 * shared `.nav-btn` / `.modal-close-btn` interaction.
 */
const TOOLBAR_BTN_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 10px",
  background: "var(--bg-card)",
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.25)",
  borderRadius: 8,
  color: "var(--text-secondary)",
  fontSize: "var(--fs-label)",
  fontWeight: 600,
  letterSpacing: 1,
  cursor: "pointer",
  transition: "border-color 0.15s, color 0.15s, background 0.15s",
};

/**
 * Destructive variant — same shape as `TOOLBAR_BTN_STYLE` but uses the
 * melon palette to match the legacy "CLEAR" button's warning tone
 * (`_legacy/wd.html` line 963 used a red-tinted variant; in DXC light
 * theme `--accent-melon` is the analogous warning slot).
 */
const TOOLBAR_DANGER_BTN_STYLE: CSSProperties = {
  ...TOOLBAR_BTN_STYLE,
  background: "rgba(var(--accent-melon-rgb), 0.08)",
  border: "1px solid rgba(var(--accent-melon-rgb), 0.30)",
  color: "var(--accent-melon)",
};

function onToolbarBtnHover(e: ReactMouseEvent<HTMLButtonElement>): void {
  if (e.currentTarget.disabled) return;
  e.currentTarget.style.borderColor = "var(--accent-true-blue)";
  e.currentTarget.style.color = "var(--accent-true-blue)";
  e.currentTarget.style.background =
    "rgba(var(--accent-true-blue-rgb), 0.06)";
}

function onToolbarBtnLeave(e: ReactMouseEvent<HTMLButtonElement>): void {
  e.currentTarget.style.borderColor =
    "rgba(var(--accent-true-blue-rgb), 0.25)";
  e.currentTarget.style.color = "var(--text-secondary)";
  e.currentTarget.style.background = "var(--bg-card)";
}

function onDangerBtnHover(e: ReactMouseEvent<HTMLButtonElement>): void {
  if (e.currentTarget.disabled) return;
  e.currentTarget.style.borderColor = "var(--accent-melon)";
  e.currentTarget.style.background =
    "rgba(var(--accent-melon-rgb), 0.14)";
}

function onDangerBtnLeave(e: ReactMouseEvent<HTMLButtonElement>): void {
  e.currentTarget.style.borderColor =
    "rgba(var(--accent-melon-rgb), 0.30)";
  e.currentTarget.style.background =
    "rgba(var(--accent-melon-rgb), 0.08)";
}

/**
 * "SAVE TO CAPTURES" pill — green accent matching the legacy primary
 * action button at `_legacy/wd.html` line 964. Sits in the top toolbar
 * so the snapshot affordance reads as the highlight action of the row.
 */
const SAVE_TO_CAPTURES_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 10px",
  background: "rgba(var(--accent-green-rgb), 0.10)",
  border: "1px solid rgba(var(--accent-green-rgb), 0.35)",
  borderRadius: 8,
  color: "var(--accent-green)",
  fontFamily: "'Inter', sans-serif",
  fontSize: "var(--fs-label)",
  fontWeight: 600,
  letterSpacing: 1,
  cursor: "pointer",
  transition: "background 0.2s, border-color 0.2s, color 0.2s",
};

function onSaveToCapturesHover(e: ReactMouseEvent<HTMLButtonElement>): void {
  if (e.currentTarget.disabled) return;
  e.currentTarget.style.background =
    "rgba(var(--accent-green-rgb), 0.18)";
  e.currentTarget.style.borderColor =
    "rgba(var(--accent-green-rgb), 0.55)";
}

function onSaveToCapturesLeave(e: ReactMouseEvent<HTMLButtonElement>): void {
  e.currentTarget.style.background =
    "rgba(var(--accent-green-rgb), 0.10)";
  e.currentTarget.style.borderColor =
    "rgba(var(--accent-green-rgb), 0.35)";
}
